import { Injectable } from '@angular/core';
import _ from 'lodash';
import { from } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BxUnverifiedAlertService } from '../../../../../../core/services/bx-unverified-alert.service';
import { SharedDataService } from '../../../../../../data/services/shared-data.service';
import { ReportConfig } from '../../../../../../reports/domain';
import { ReportService } from '../../../../../../reports/report.service';
import { EstimatesService } from '../../../../estimates.service';
import { EstimateDetails, EstimateViewStoresService } from '../../../shell';
import { EstimateMeasurementPrintModes } from './domain';

@Injectable({
    providedIn: 'root',
})
export class EstimateMeasurementsPrintingService {
    // #region constructor
    constructor(
        private readonly reportService: ReportService,
        private readonly estimateViewStoresService: EstimateViewStoresService,
        private readonly bxUnverifiedAlertService: BxUnverifiedAlertService,
        private readonly sharedData: SharedDataService,
        private readonly estimatesService: EstimatesService
    ) {}
    // #endregion constructor

    // #region print
    public printMeasurements(mode: EstimateMeasurementPrintModes) {
        this.estimateViewStoresService.estimate$
            .pipe(
                take(1),
                // @ts-ignore TS2345
                map((estimate: EstimateDetails) => this.getMeasurementsReportParams(estimate, mode, null))
            )
            .subscribe((config: ReportConfig) => {
                this.reportService.generate(config);
            });
    }
    // #endregion print

    // #region email
    public emailMeasurements(mode: EstimateMeasurementPrintModes) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.estimateViewStoresService.estimate$
            .pipe(
                take(1),
                map((estimate: EstimateDetails) => ({ estimate, config: this.getMeasurementsReportParams(estimate, mode, 'pdf') })),
                switchMap((result: { estimate: EstimateDetails; config: ReportConfig }) =>
                    // @ts-ignore TS2345
                    from(this.reportService.generate(result.config)).pipe(
                        map((response) => ({
                            estimate: result.estimate,
                            attachment: response.data,
                        }))
                    )
                )
            )
            .subscribe((result) => this.emailEstimate(mode, result));
    }

    private emailEstimate(mode: EstimateMeasurementPrintModes, result: { estimate: EstimateDetails; attachment: any }) {
        const emailParams = {
            Subject: `${result.estimate.FullDescription} - Measurements${
                mode === EstimateMeasurementPrintModes.Breakdown ? ' Breakdown' : ''
            }`,
            Body: result.estimate.Name ? `Dear ${_.escape(result.estimate.Name)},` : '',
            StorageAttachments: [result.attachment],
        };

        this.estimatesService.emailEstimate(result.estimate, emailParams);
    }
    // #endregion email

    private getMeasurementsReportParams(estimate: EstimateDetails, mode: EstimateMeasurementPrintModes, format: string): ReportConfig {
        const config: ReportConfig = {
            reportName: mode !== EstimateMeasurementPrintModes.Breakdown ? 'estimatemeasurements' : 'estimatemeasurementsbreakdown',
            reportTitle: `Take Off Measurements${mode === EstimateMeasurementPrintModes.Breakdown ? ' Breakdown' : ''}`,
            params: {
                ID_Estimate: estimate.ID_Estimate,
            },
        };

        if (estimate.Number) {
            config.fileName = `${estimate.Number}-Measurements${mode === EstimateMeasurementPrintModes.Breakdown ? 'Breakdown' : ''}`;
        }

        if (format) {
            config.format = format;
        }

        return config;
    }
}
